import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { 
  RefreshCw, Edit, Clock, History, AlertTriangle, CheckCircle, 
  X, Save, ChevronDown, ChevronUp, Mail, Phone, Calendar, 
  DollarSign, Ticket, User, Search, Filter, TicketCheck, Banknote, Send, CreditCard, Copy, Plus, Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import TransactionSteps from './TransactionSteps';
import AdminTransactionTimeline from './AdminTransactionTimeline';
import CopyableText, { CopyableBox } from './CopyableText';
import AdminTransferForm from './AdminTransferForm';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TransactionManagement = ({ token }) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewMode, setViewMode] = useState('active'); // 'active' | 'cancelled'
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  
  // Dialogs
  const [showReactivateDialog, setShowReactivateDialog] = useState(false);
  const [showModifyDialog, setShowModifyDialog] = useState(false);
  const [showExpireDialog, setShowExpireDialog] = useState(false);
  const [showStepsDialog, setShowStepsDialog] = useState(false);
  const [showVerifyTicketsDialog, setShowVerifyTicketsDialog] = useState(false);
  const [showConfirmPaymentDialog, setShowConfirmPaymentDialog] = useState(false);
  const [showTransferTicketsDialog, setShowTransferTicketsDialog] = useState(false);
  const [showSendPaymentDialog, setShowSendPaymentDialog] = useState(false);
  const [showTicketsNonCompliantDialog, setShowTicketsNonCompliantDialog] = useState(false);
  const [showPaymentNonCompliantDialog, setShowPaymentNonCompliantDialog] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  
  // Form data
  const [reactivateReason, setReactivateReason] = useState('');
  const [reactivateDeadline, setReactivateDeadline] = useState(48);
  const [modifyData, setModifyData] = useState({});
  const [actionLoading, setActionLoading] = useState(false);
  const [nonCompliantReason, setNonCompliantReason] = useState('');
  const [paymentExpected, setPaymentExpected] = useState('');
  const [paymentReceived, setPaymentReceived] = useState('');

  useEffect(() => {
    fetchTransactions();
  }, []);
  
  const fetchTransactions = async () => {
    try {
      const response = await fetch(`${API}/transactions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTransactions(data);
      }
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
      toast.error('Erreur lors du chargement des transferts');
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async (transactionId) => {
    setLoadingHistory(true);
    try {
      const response = await fetch(`${API}/admin/transactions/${transactionId}/history`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setHistory(data.history || []);
      }
    } catch (error) {
      console.error('Failed to fetch history:', error);
      toast.error('Erreur lors du chargement de l\'historique');
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleDeleteTransaction = async () => {
    if (!selectedTransaction) return;
    
    setActionLoading(true);
    try {
      const response = await fetch(`${API}/super-admin/transactions/${selectedTransaction.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Transfert supprimé avec succès');
        setShowDeleteDialog(false);
        setSelectedTransaction(null);
        fetchTransactions();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la suppression');
      }
    } catch (error) {
      console.error('Failed to delete transaction:', error);
      toast.error('Erreur lors de la suppression du transfert');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivate = async () => {
    if (!selectedTransaction || !reactivateReason.trim()) {
      toast.error('Veuillez entrer une raison pour la réactivation');
      return;
    }
    
    setActionLoading(true);
    try {
      const response = await fetch(`${API}/admin/transactions/${selectedTransaction.id}/reactivate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          reason: reactivateReason,
          new_deadline_hours: reactivateDeadline
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success('Transfert réactivé avec succès! Nouveaux emails de confirmation envoyés.');
        setShowReactivateDialog(false);
        setReactivateReason('');
        fetchTransactions();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la réactivation');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setActionLoading(false);
    }
  };

  const handleModify = async () => {
    if (!selectedTransaction) return;
    
    // Filter out empty values
    const updates = Object.fromEntries(
      Object.entries(modifyData).filter(([_, v]) => v !== '' && v !== null)
    );
    
    if (Object.keys(updates).length === 0) {
      toast.error('Aucune modification à effectuer');
      return;
    }
    
    setActionLoading(true);
    try {
      const response = await fetch(`${API}/admin/transactions/${selectedTransaction.id}/modify`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Transfert modifié: ${data.changes.length} changement(s)`);
        setShowModifyDialog(false);
        setModifyData({});
        fetchTransactions();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la modification');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setActionLoading(false);
    }
  };

  const handleExpire = async () => {
    if (!selectedTransaction) return;
    
    setActionLoading(true);
    try {
      const response = await fetch(`${API}/admin/transactions/${selectedTransaction.id}/expire`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Transfert marqué comme expiré');
        setShowExpireDialog(false);
        fetchTransactions();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de l\'expiration');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setActionLoading(false);
    }
  };

  const handleVerifyTickets = async () => {
    if (!selectedTransaction) return;
    
    setActionLoading(true);
    try {
      const response = await fetch(`${API}/admin/transactions/${selectedTransaction.id}/verify-tickets`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success('Billets vérifiés! Notifications envoyées à l\'acheteur et au vendeur.');
        setShowVerifyTicketsDialog(false);
        fetchTransactions();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la vérification');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setActionLoading(false);
    }
  };

  const handleConfirmPayment = async () => {
    if (!selectedTransaction) return;
    
    setActionLoading(true);
    try {
      const response = await fetch(`${API}/admin/transactions/${selectedTransaction.id}/confirm-interac-payment`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success('Paiement Interac confirmé! Notifications envoyées.');
        setShowConfirmPaymentDialog(false);
        fetchTransactions();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la confirmation du paiement');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setActionLoading(false);
    }
  };

  const handleTransferTickets = async () => {
    if (!selectedTransaction) return;
    
    setActionLoading(true);
    try {
      const response = await fetch(`${API}/admin/transactions/${selectedTransaction.id}/transfer-tickets-to-buyer`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success('Billets transférés à l\'acheteur! Notification envoyée.');
        setShowTransferTicketsDialog(false);
        fetchTransactions();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors du transfert des billets');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSendPayment = async () => {
    if (!selectedTransaction) return;
    
    setActionLoading(true);
    try {
      const response = await fetch(`${API}/admin/transactions/${selectedTransaction.id}/send-payment-to-seller`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Paiement de ${data.amount}$ envoyé au vendeur (${data.seller_interac_email})!`);
        setShowSendPaymentDialog(false);
        fetchTransactions();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de l\'envoi du paiement');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setActionLoading(false);
    }
  };

  const handleTicketsNonCompliant = async () => {
    if (!selectedTransaction) return;
    
    setActionLoading(true);
    try {
      const response = await fetch(`${API}/admin/transactions/${selectedTransaction.id}/tickets-non-compliant`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason: nonCompliantReason })
      });
      
      if (response.ok) {
        toast.success('Billets marqués comme non conformes. Notification envoyée au vendeur.');
        setShowTicketsNonCompliantDialog(false);
        setNonCompliantReason('');
        fetchTransactions();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setActionLoading(false);
    }
  };

  const handlePaymentNonCompliant = async () => {
    if (!selectedTransaction || !paymentExpected || !paymentReceived) return;
    
    setActionLoading(true);
    try {
      const response = await fetch(`${API}/admin/transactions/${selectedTransaction.id}/payment-non-compliant`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          expected_amount: parseFloat(paymentExpected),
          received_amount: parseFloat(paymentReceived)
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        const diff = data.difference;
        toast.success(`Paiement non conforme. Différence: ${diff > 0 ? '+' : ''}${diff.toFixed(2)}$. Notification envoyée à l'acheteur.`);
        setShowPaymentNonCompliantDialog(false);
        setPaymentExpected('');
        setPaymentReceived('');
        fetchTransactions();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setActionLoading(false);
    }
  };

  const openModifyDialog = (transaction) => {
    setSelectedTransaction(transaction);
    setModifyData({
      buyer_email: '',
      seller_email: '',
      buyer_phone: '',
      seller_phone: '',
      event_name: '',
      event_date: '',
      ticket_count: '',
      price: '',
      total_amount: '',
      admin_notes: ''
    });
    setShowModifyDialog(true);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'pending_confirmation': { label: 'En attente de confirmation', color: 'bg-amber-100 text-amber-800' },
      'confirmed': { label: 'Confirmé', color: 'bg-blue-100 text-blue-800' },
      'payment_pending': { label: 'Paiement en attente', color: 'bg-purple-100 text-purple-800' },
      'payment_received': { label: 'Paiement reçu', color: 'bg-green-100 text-green-800' },
      'payment_completed': { label: 'Paiement complété', color: 'bg-green-100 text-green-800' },
      'tickets_verified': { label: 'Billets vérifiés', color: 'bg-brand-100 text-brand-800' },
      'tickets_transferred': { label: 'Billets transférés', color: 'bg-cyan-100 text-cyan-800' },
      'payment_sent': { label: 'Paiement envoyé', color: 'bg-emerald-100 text-emerald-800' },
      'tickets_released': { label: 'Billets transférés', color: 'bg-brand-100 text-brand-800' },
      'completed': { label: 'Complété', color: 'bg-green-100 text-green-800' },
      'expired': { label: 'Expiré', color: 'bg-red-100 text-red-800' },
      'cancelled': { label: 'Annulé', color: 'bg-gray-100 text-gray-800' }
    };
    const config = statusConfig[status] || { label: status, color: 'bg-gray-100 text-gray-800' };
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>{config.label}</span>;
  };

  // Calculate step completion progress for a transaction
  const getStepProgress = (transaction) => {
    const steps = [
      { key: 'buyer_confirmed', label: 'Acheteur' },
      { key: 'seller_confirmed', label: 'Vendeur' },
      { key: 'payment_completed', label: 'Paiement' },
      { key: 'tickets_verified', label: 'Billets vérifiés' },
      { key: 'tickets_transferred_to_buyer', label: 'Billets transférés' },
      { key: 'payment_sent_to_seller', label: 'Paiement envoyé' }
    ];
    
    let completed = 0;
    steps.forEach(step => {
      if (transaction[step.key]) completed++;
    });
    
    return { completed, total: steps.length };
  };

  // Get progress bar color based on completion
  const getProgressColor = (completed, total) => {
    const percentage = (completed / total) * 100;
    if (percentage === 100) return 'bg-green-500';
    if (percentage >= 66) return 'bg-brand-500';
    if (percentage >= 33) return 'bg-amber-500';
    return 'bg-gray-300';
  };

  const isCancelled = (t) => t.status === 'cancelled' || t.cancelled === true;

  const filteredTransactions = transactions.filter(t => {
    const matchesSearch = 
      t.event_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.buyer_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.seller_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.reference_number?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || t.status === statusFilter;
    
    // Split by view mode: active tab hides cancelled, cancelled tab only shows cancelled
    const matchesViewMode = viewMode === 'cancelled' ? isCancelled(t) : !isCancelled(t);
    
    return matchesSearch && matchesStatus && matchesViewMode;
  });

  const cancelledCount = transactions.filter(isCancelled).length;
  const activeCount = transactions.length - cancelledCount;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* View mode tabs: Active vs Cancelled */}
      <div className="flex flex-wrap gap-2 border-b border-gray-200 pb-2" data-testid="transactions-view-tabs">
        <button
          type="button"
          onClick={() => setViewMode('active')}
          className={`px-4 py-2 text-sm font-medium rounded-t-md transition-colors ${
            viewMode === 'active'
              ? 'bg-brand-600 text-white shadow-sm'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
          data-testid="tab-active-transactions"
        >
          <span className="inline-flex items-center gap-2">
            <CheckCircle className="h-4 w-4" />
            Actives
            <span className={`ml-1 px-2 py-0.5 rounded-full text-xs font-bold ${
              viewMode === 'active' ? 'bg-white/20 text-white' : 'bg-white text-gray-700'
            }`}>{activeCount}</span>
          </span>
        </button>
        <button
          type="button"
          onClick={() => setViewMode('cancelled')}
          className={`px-4 py-2 text-sm font-medium rounded-t-md transition-colors ${
            viewMode === 'cancelled'
              ? 'bg-red-600 text-white shadow-sm'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
          data-testid="tab-cancelled-transactions"
        >
          <span className="inline-flex items-center gap-2">
            <X className="h-4 w-4" />
            Annulées
            <span className={`ml-1 px-2 py-0.5 rounded-full text-xs font-bold ${
              viewMode === 'cancelled' ? 'bg-white/20 text-white' : 'bg-red-100 text-red-700'
            }`}>{cancelledCount}</span>
          </span>
        </button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Rechercher par événement, email, réf..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            {viewMode === 'active' && (
              <div className="w-full sm:w-[200px]">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="all">Tous les statuts</option>
                  <option value="pending_confirmation">En attente de confirmation</option>
                  <option value="confirmed">Confirmé</option>
                  <option value="payment_pending">Paiement en attente</option>
                  <option value="payment_received">Paiement reçu</option>
                  <option value="payment_completed">Paiement complété</option>
                  <option value="tickets_verified">Billets vérifiés</option>
                  <option value="completed">Complété</option>
                  <option value="expired">Expiré</option>
                </select>
              </div>
            )}
            <Button onClick={fetchTransactions} variant="outline" className="w-full sm:w-auto">
              <RefreshCw className="h-4 w-4 mr-2" />
              Actualiser
            </Button>
            <Button 
              onClick={() => setShowCreateDialog(true)} 
              className="bg-[#67ffbb] hover:bg-[#4de6a8] text-gray-900 btn-animated w-full sm:w-auto"
            >
              <Plus className="h-4 w-4 mr-2" />
              Créer un transfert
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Transactions List */}
      <Card className={viewMode === 'cancelled' ? 'border-red-200' : ''}>
        <CardHeader className={viewMode === 'cancelled' ? 'bg-red-50' : ''}>
          <CardTitle className="flex items-center gap-2">
            {viewMode === 'cancelled' ? (
              <>
                <X className="h-5 w-5 text-red-600" />
                <span className="text-red-800">Transactions annulées ({filteredTransactions.length})</span>
              </>
            ) : (
              <>
                <Ticket className="h-5 w-5 text-brand-600" />
                <span>Transferts actifs ({filteredTransactions.length})</span>
              </>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredTransactions.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              {viewMode === 'cancelled' ? 'Aucune transaction annulée' : 'Aucun transfert trouvé'}
            </div>
          ) : (
            <div className="space-y-4">
              {filteredTransactions.map((transaction) => (
                <div
                  key={transaction.id}
                  className={`border rounded-lg p-3 sm:p-4 transition-colors ${
                    isCancelled(transaction)
                      ? 'border-red-300 bg-red-50/40 hover:border-red-400'
                      : 'hover:border-brand-300'
                  }`}
                  data-testid={`transaction-row-${transaction.id}`}
                >
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3 lg:gap-4">
                    {/* Transaction Info */}
                    <div className="flex-1 min-w-0 w-full">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <h4 className="font-semibold text-gray-900 break-words text-sm sm:text-base">{transaction.event_name}</h4>
                        {getStatusBadge(transaction.status)}
                        {isCancelled(transaction) && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-red-600 text-white">
                            ANNULÉE
                          </span>
                        )}
                        {/* Step Progress Indicator */}
                        {transaction.status !== 'expired' && !isCancelled(transaction) && (
                          <div className="flex items-center gap-1.5" title={`${getStepProgress(transaction).completed}/${getStepProgress(transaction).total} étapes complétées`}>
                            <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                              <div 
                                className={`h-full ${getProgressColor(getStepProgress(transaction).completed, getStepProgress(transaction).total)} transition-all duration-300`}
                                style={{ width: `${(getStepProgress(transaction).completed / getStepProgress(transaction).total) * 100}%` }}
                              />
                            </div>
                            <span className="text-xs text-gray-500 font-medium">
                              {getStepProgress(transaction).completed}/{getStepProgress(transaction).total}
                            </span>
                          </div>
                        )}
                      </div>
                      
                      {/* Cancellation reason banner if cancelled */}
                      {isCancelled(transaction) && transaction.cancellation_reason && (
                        <div className="mb-2 p-2 rounded bg-red-100 border border-red-200 text-xs text-red-900">
                          <strong>Raison de l&apos;annulation :</strong> {transaction.cancellation_reason}
                        </div>
                      )}
                      
                      {/* Info Grid - Stack on mobile */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 sm:gap-2 text-sm">
                        <div className="flex items-center gap-2 text-gray-600 min-w-0">
                          <Calendar className="h-4 w-4 flex-shrink-0" />
                          <span className="truncate">{transaction.event_date}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600 min-w-0">
                          <DollarSign className="h-4 w-4 flex-shrink-0" />
                          <span className="truncate">{transaction.price}$ ({transaction.ticket_count} billet{transaction.ticket_count > 1 ? 's' : ''})</span>
                        </div>
                        
                        {/* Acheteur - Full width on mobile */}
                        <div className="flex flex-wrap items-center gap-1 text-gray-600 sm:col-span-2 min-w-0">
                          <User className="h-4 w-4 text-brand-500 flex-shrink-0" />
                          <span className="text-xs sm:text-sm font-medium">Acheteur:</span>
                          <span className="text-xs sm:text-sm truncate max-w-[130px] sm:max-w-[200px] min-w-0">{transaction.buyer_email?.split('@')[0]}@...</span>
                          {transaction.buyer_client_number && (
                            <span className="px-1.5 py-0.5 bg-brand-100 text-brand-700 text-xs font-mono font-bold rounded">
                              #{transaction.buyer_client_number}
                            </span>
                          )}
                          {transaction.buyer_confirmed && <CheckCircle className="h-3 w-3 text-green-500 flex-shrink-0" />}
                        </div>
                        
                        {/* Vendeur - Full width on mobile */}
                        <div className="flex flex-wrap items-center gap-1 text-gray-600 sm:col-span-2 min-w-0">
                          <User className="h-4 w-4 text-purple-500 flex-shrink-0" />
                          <span className="text-xs sm:text-sm font-medium">Vendeur:</span>
                          <span className="text-xs sm:text-sm truncate max-w-[130px] sm:max-w-[200px] min-w-0">{transaction.seller_email?.split('@')[0]}@...</span>
                          {transaction.seller_client_number && (
                            <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 text-xs font-mono font-bold rounded">
                              #{transaction.seller_client_number}
                            </span>
                          )}
                          {transaction.seller_confirmed && <CheckCircle className="h-3 w-3 text-green-500 flex-shrink-0" />}
                        </div>
                      </div>
                      
                      {/* Reference & ID - Wrap properly */}
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] sm:text-xs text-gray-400 mt-2">
                        {transaction.reference_number && (
                          <span className="font-mono font-semibold text-brand-600">{transaction.reference_number}</span>
                        )}
                        <span className="hidden sm:inline">ID: {transaction.id?.slice(0, 8)}...</span>
                        <span>Créé: {new Date(transaction.created_at).toLocaleDateString('fr-CA')}</span>
                      </div>
                    </div>

                    {/* Actions - Full width on mobile, organized in rows */}
                    <div className="flex flex-col gap-2 w-full lg:w-auto lg:flex-shrink-0">
                      {/* Row 1: Basic Actions - 2 col on mobile, wrap on desktop */}
                      <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedTransaction(transaction);
                            setShowStepsDialog(true);
                          }}
                          data-testid={`view-steps-${transaction.id}`}
                          className="w-full sm:w-auto"
                        >
                          <Ticket className="h-4 w-4 mr-1" />
                          Étapes
                        </Button>
                        
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedTransaction(transaction);
                            fetchHistory(transaction.id);
                            setShowHistory(true);
                          }}
                          data-testid={`view-history-${transaction.id}`}
                          className="w-full sm:w-auto"
                        >
                          <History className="h-4 w-4 mr-1" />
                          Historique
                        </Button>
                        
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openModifyDialog(transaction)}
                          data-testid={`modify-${transaction.id}`}
                          className="w-full sm:w-auto"
                        >
                          <Edit className="h-4 w-4 mr-1" />
                          Modifier
                        </Button>
                        
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-red-500 text-red-600 hover:bg-red-50 w-full sm:w-auto"
                          onClick={() => {
                            setSelectedTransaction(transaction);
                            setShowDeleteDialog(true);
                          }}
                          data-testid={`delete-${transaction.id}`}
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Supprimer
                        </Button>
                      </div>
                      
                      {/* Row 2: Tickets Actions (always visible when transaction is active) */}
                      {/* Note: All action buttons (verify tickets, confirm payment, etc.) are now only in the detailed timeline view */}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Reactivate Dialog */}
      <AlertDialog open={showReactivateDialog} onOpenChange={setShowReactivateDialog}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-green-600">
              <RefreshCw className="h-5 w-5" />
              Réactiver le transfert
            </AlertDialogTitle>
            <AlertDialogDescription className="text-left space-y-4">
              <p>
                Vous allez réactiver le transfert pour <strong>{selectedTransaction?.event_name}</strong>.
              </p>
              <p className="text-sm text-gray-600">
                De nouveaux emails de confirmation seront envoyés aux deux parties.
              </p>
              
              <div className="space-y-3">
                <div>
                  <Label>Raison de la réactivation *</Label>
                  <Input
                    value={reactivateReason}
                    onChange={(e) => setReactivateReason(e.target.value)}
                    placeholder="Ex: Demande du client après support"
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label>Nouveau délai (heures)</Label>
                  <Input
                    type="number"
                    value={reactivateDeadline}
                    onChange={(e) => setReactivateDeadline(parseInt(e.target.value) || 48)}
                    min={1}
                    max={168}
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Le transfert expirera automatiquement après ce délai si non confirmé.
                  </p>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionLoading}>Annuler</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleReactivate}
              className="bg-green-600 hover:bg-green-700"
              disabled={actionLoading || !reactivateReason.trim()}
            >
              {actionLoading ? 'Réactivation...' : 'Réactiver'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modify Dialog */}
      <AlertDialog open={showModifyDialog} onOpenChange={setShowModifyDialog}>
        <AlertDialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-blue-600">
              <Edit className="h-5 w-5" />
              Modifier le transfert
            </AlertDialogTitle>
            <AlertDialogDescription className="text-left">
              <p className="mb-4">
                Modifiez les informations du transfert pour <strong>{selectedTransaction?.event_name}</strong>.
                Laissez vide les champs que vous ne souhaitez pas modifier.
              </p>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    Email acheteur
                  </Label>
                  <Input
                    value={modifyData.buyer_email || ''}
                    onChange={(e) => setModifyData({...modifyData, buyer_email: e.target.value})}
                    placeholder={selectedTransaction?.buyer_email}
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label className="flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    Email vendeur
                  </Label>
                  <Input
                    value={modifyData.seller_email || ''}
                    onChange={(e) => setModifyData({...modifyData, seller_email: e.target.value})}
                    placeholder={selectedTransaction?.seller_email}
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label className="flex items-center gap-2">
                    <Phone className="h-4 w-4" />
                    Téléphone acheteur
                  </Label>
                  <Input
                    value={modifyData.buyer_phone || ''}
                    onChange={(e) => setModifyData({...modifyData, buyer_phone: e.target.value})}
                    placeholder={selectedTransaction?.buyer_phone || 'Non renseigné'}
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label className="flex items-center gap-2">
                    <Phone className="h-4 w-4" />
                    Téléphone vendeur
                  </Label>
                  <Input
                    value={modifyData.seller_phone || ''}
                    onChange={(e) => setModifyData({...modifyData, seller_phone: e.target.value})}
                    placeholder={selectedTransaction?.seller_phone || 'Non renseigné'}
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label className="flex items-center gap-2">
                    <Ticket className="h-4 w-4" />
                    Nom de l'événement
                  </Label>
                  <Input
                    value={modifyData.event_name || ''}
                    onChange={(e) => setModifyData({...modifyData, event_name: e.target.value})}
                    placeholder={selectedTransaction?.event_name}
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    Date de l'événement
                  </Label>
                  <Input
                    type="date"
                    value={modifyData.event_date || ''}
                    onChange={(e) => setModifyData({...modifyData, event_date: e.target.value})}
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label className="flex items-center gap-2">
                    <Ticket className="h-4 w-4" />
                    Nombre de billets
                  </Label>
                  <Input
                    type="number"
                    value={modifyData.ticket_count || ''}
                    onChange={(e) => setModifyData({...modifyData, ticket_count: parseInt(e.target.value) || ''})}
                    placeholder={selectedTransaction?.ticket_count?.toString()}
                    min={1}
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label className="flex items-center gap-2">
                    <DollarSign className="h-4 w-4" />
                    Prix
                  </Label>
                  <Input
                    type="number"
                    value={modifyData.price || ''}
                    onChange={(e) => setModifyData({...modifyData, price: parseFloat(e.target.value) || ''})}
                    placeholder={selectedTransaction?.price?.toString()}
                    step="0.01"
                    className="mt-1"
                  />
                </div>
                
                <div className="col-span-2">
                  <Label>Notes admin</Label>
                  <textarea
                    value={modifyData.admin_notes || ''}
                    onChange={(e) => setModifyData({...modifyData, admin_notes: e.target.value})}
                    placeholder="Raison de la modification..."
                    className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md min-h-[80px]"
                  />
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionLoading}>Annuler</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleModify}
              className="bg-blue-600 hover:bg-blue-700"
              disabled={actionLoading}
            >
              {actionLoading ? 'Modification...' : 'Enregistrer les modifications'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Expire Dialog */}
      <AlertDialog open={showExpireDialog} onOpenChange={setShowExpireDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Expirer le transfert
            </AlertDialogTitle>
            <AlertDialogDescription>
              <p>
                Êtes-vous sûr de vouloir expirer le transfert pour <strong>{selectedTransaction?.event_name}</strong>?
              </p>
              <p className="mt-2 text-amber-600">
                Les deux parties seront notifiées. Cette action peut être annulée en réactivant le transfert.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionLoading}>Annuler</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleExpire}
              className="bg-red-600 hover:bg-red-700"
              disabled={actionLoading}
            >
              {actionLoading ? 'Expiration...' : 'Expirer'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* History Dialog */}
      <AlertDialog open={showHistory} onOpenChange={setShowHistory}>
        <AlertDialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5 text-gray-600" />
              Historique des actions admin
            </AlertDialogTitle>
          </AlertDialogHeader>
          
          <div className="py-4">
            {loadingHistory ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-600"></div>
              </div>
            ) : history.length === 0 ? (
              <p className="text-center text-gray-500 py-4">Aucune action admin enregistrée</p>
            ) : (
              <div className="space-y-3">
                {history.map((entry, index) => (
                  <div key={index} className="border rounded-lg p-3 bg-gray-50">
                    <div className="flex items-center justify-between mb-2">
                      <span className={`text-sm font-medium px-2 py-0.5 rounded ${
                        entry.action === 'reactivated' ? 'bg-green-100 text-green-800' :
                        entry.action === 'modified' ? 'bg-blue-100 text-blue-800' :
                        entry.action === 'manually_expired' || entry.action === 'auto_expired' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {entry.action === 'reactivated' ? 'Réactivé' :
                         entry.action === 'modified' ? 'Modifié' :
                         entry.action === 'manually_expired' ? 'Expiré manuellement' :
                         entry.action === 'auto_expired' ? 'Expiré automatiquement' :
                         entry.action}
                      </span>
                      <span className="text-xs text-gray-500">
                        {new Date(entry.timestamp).toLocaleString('fr-CA')}
                      </span>
                    </div>
                    
                    {entry.performed_by && (
                      <p className="text-sm text-gray-600">
                        Par: {entry.performed_by}
                      </p>
                    )}
                    
                    {entry.reason && (
                      <p className="text-sm text-gray-600 mt-1">
                        Raison: {entry.reason}
                      </p>
                    )}
                    
                    {entry.changes && entry.changes.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs font-medium text-gray-500 mb-1">Modifications:</p>
                        <ul className="text-xs text-gray-600 space-y-0.5">
                          {entry.changes.map((change, idx) => (
                            <li key={idx}>• {change}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <AlertDialogFooter>
            <AlertDialogCancel>Fermer</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Steps Dialog */}
      <AlertDialog open={showStepsDialog} onOpenChange={setShowStepsDialog}>
        <AlertDialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-0">
          {/* Sticky header with close button */}
          <div className="sticky top-0 z-10 bg-white border-b px-4 py-3 flex items-center justify-between gap-2">
            <AlertDialogTitle className="flex items-center gap-2 text-base sm:text-lg font-bold m-0">
              <Ticket className="h-5 w-5 text-brand-600 shrink-0" />
              <span className="truncate">{selectedTransaction?.event_name}</span>
            </AlertDialogTitle>
            <AlertDialogCancel 
              className="m-0 p-2 h-auto border-0 hover:bg-gray-100 rounded-full shrink-0"
              data-testid="close-timeline-btn"
            >
              <X className="h-5 w-5" />
            </AlertDialogCancel>
          </div>
          
          <div className="p-4">
            <AdminTransactionTimeline 
              transaction={selectedTransaction} 
              token={token}
              onUpdate={async (updatedTransaction) => {
                if (updatedTransaction && updatedTransaction.id) {
                  setSelectedTransaction(updatedTransaction);
                  setTransactions(prev => prev.map(t => 
                    t.id === updatedTransaction.id ? updatedTransaction : t
                  ));
                }
                await fetchTransactions();
              }}
            />
          </div>
          
          {/* Bottom close button for easy access */}
          <div className="sticky bottom-0 bg-white border-t px-4 py-3 flex justify-end">
            <AlertDialogCancel data-testid="close-timeline-bottom-btn">Fermer</AlertDialogCancel>
          </div>
        </AlertDialogContent>
      </AlertDialog>

      {/* Verify Tickets Dialog */}
      <AlertDialog open={showVerifyTicketsDialog} onOpenChange={setShowVerifyTicketsDialog}>
        <AlertDialogContent className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-brand-600">
              <TicketCheck className="h-5 w-5" />
              Confirmer la réception des billets
            </AlertDialogTitle>
            <AlertDialogDescription className="text-left space-y-4">
              <p>
                Confirmez que vous avez reçu les billets pour <strong>{selectedTransaction?.event_name}</strong> dans votre compte de billetterie et qu'ils correspondent à la description.
              </p>
              
              <div className="bg-brand-50 border border-brand-200 rounded-lg p-4 space-y-2">
                <p className="font-medium text-brand-800">Vérifications à effectuer:</p>
                <ul className="text-sm text-brand-700 space-y-1 list-disc list-inside">
                  <li>Les billets sont dans votre compte de billetterie</li>
                  <li>L'événement correspond: <strong>{selectedTransaction?.event_name}</strong></li>
                  <li>La date correspond: <strong>{selectedTransaction?.event_date}</strong></li>
                  <li>Le nombre de billets: <strong>{selectedTransaction?.ticket_count}</strong></li>
                  {selectedTransaction?.seat_section && (
                    <li>Section: <strong>{selectedTransaction?.seat_section}</strong></li>
                  )}
                  {selectedTransaction?.seat_row && (
                    <li>Rangée: <strong>{selectedTransaction?.seat_row}</strong></li>
                  )}
                </ul>
              </div>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800">
                  <strong>⚠️ Important:</strong> En confirmant, vous attestez que les billets ont été transférés 
                  dans le compte Middleman et sont authentiques. Les notifications seront envoyées 
                  à l'acheteur et au vendeur.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionLoading}>Annuler</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleVerifyTickets}
              className="bg-brand-600 hover:bg-brand-700"
              disabled={actionLoading}
            >
              {actionLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Vérification...
                </>
              ) : (
                <>
                  <TicketCheck className="h-4 w-4 mr-2" />
                  Confirmer les billets
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirm Interac Payment Dialog */}
      <AlertDialog open={showConfirmPaymentDialog} onOpenChange={setShowConfirmPaymentDialog}>
        <AlertDialogContent className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-green-600">
              <Banknote className="h-5 w-5" />
              Confirmer le paiement Interac
            </AlertDialogTitle>
            <AlertDialogDescription className="text-left space-y-4">
              <p>
                Confirmez que vous avez reçu le virement Interac pour le transfert <strong>{selectedTransaction?.event_name}</strong>.
              </p>
              
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-2">
                <p className="font-medium text-green-800">Détails du transfert:</p>
                <div className="text-sm text-green-700 space-y-1">
                  <p>• Événement: <strong>{selectedTransaction?.event_name}</strong></p>
                  <p>• Montant attendu: <strong>{selectedTransaction?.total_amount || selectedTransaction?.price}$</strong></p>
                  <p>• Acheteur: <strong>{selectedTransaction?.buyer_email}</strong></p>
                  <p>• Numéro de référence: <strong>{selectedTransaction?.id?.slice(0, 8)}</strong></p>
                </div>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  <strong>ℹ️ Vérifications à effectuer:</strong>
                </p>
                <ul className="text-sm text-blue-700 mt-2 space-y-1 list-disc list-inside">
                  <li>Le montant reçu correspond au montant total</li>
                  <li>Le nom de l'expéditeur correspond à l'acheteur</li>
                  <li>Le message contient le numéro de référence</li>
                </ul>
              </div>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800">
                  <strong>⚠️ Important:</strong> En confirmant, le vendeur sera notifié de transférer les billets via Ticketmaster à <code className="bg-amber-100 px-1 rounded">info@transfertdebillets.ca</code>
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionLoading}>Annuler</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirmPayment}
              className="bg-green-600 hover:bg-green-700"
              disabled={actionLoading}
            >
              {actionLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Confirmation...
                </>
              ) : (
                <>
                  <Banknote className="h-4 w-4 mr-2" />
                  Confirmer le paiement
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Transfer Tickets to Buyer Dialog */}
      <AlertDialog open={showTransferTicketsDialog} onOpenChange={setShowTransferTicketsDialog}>
        <AlertDialogContent className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-cyan-600">
              <Send className="h-5 w-5" />
              Transférer les billets à l'acheteur
            </AlertDialogTitle>
            <AlertDialogDescription className="text-left space-y-4">
              <p>
                Confirmez que vous avez transféré les billets pour <strong>{selectedTransaction?.event_name}</strong> à l'acheteur via Ticketmaster.
              </p>
              
              <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4 space-y-2">
                <p className="font-medium text-cyan-800">Détails du transfert:</p>
                <div className="text-sm text-cyan-700 space-y-1">
                  <p>• Événement: <strong>{selectedTransaction?.event_name}</strong></p>
                  <p>• Date: <strong>{selectedTransaction?.event_date}</strong></p>
                  <p>• Nombre de billets: <strong>{selectedTransaction?.ticket_count}</strong></p>
                  <p>• Acheteur: <strong>{selectedTransaction?.buyer_email}</strong></p>
                </div>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  <strong>ℹ️ Vérification:</strong> Assurez-vous que le transfert Ticketmaster a été complété avec succès avant de confirmer.
                </p>
              </div>
              
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <p className="text-sm text-green-800">
                  <strong>✅ Action:</strong> L'acheteur recevra un SMS et un email confirmant que ses billets sont disponibles dans son compte Ticketmaster.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionLoading}>Annuler</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleTransferTickets}
              className="bg-cyan-600 hover:bg-cyan-700"
              disabled={actionLoading}
            >
              {actionLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Transfert...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Confirmer le transfert
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Send Payment to Seller Dialog */}
      <AlertDialog open={showSendPaymentDialog} onOpenChange={setShowSendPaymentDialog}>
        <AlertDialogContent className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-emerald-600">
              <CreditCard className="h-5 w-5" />
              Envoyer le paiement au vendeur
            </AlertDialogTitle>
            <AlertDialogDescription className="text-left space-y-4">
              <p>
                Confirmez que vous avez envoyé le virement Interac au vendeur pour <strong>{selectedTransaction?.event_name}</strong>.
              </p>
              
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 space-y-2">
                <p className="font-medium text-emerald-800">Détails du paiement:</p>
                <div className="text-sm text-emerald-700 space-y-1">
                  <p>• Événement: <strong>{selectedTransaction?.event_name}</strong></p>
                  <p>• Montant à envoyer: <strong>{selectedTransaction?.price}$</strong></p>
                  <p>• Vendeur: <strong>{selectedTransaction?.seller_email}</strong></p>
                </div>
              </div>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800">
                  <strong>⚠️ Important:</strong> Le paiement doit être envoyé à l'email Interac configuré par le vendeur dans son profil. Si le vendeur n'a pas configuré son email Interac, une erreur s'affichera.
                </p>
              </div>
              
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <p className="text-sm text-green-800">
                  <strong>✅ Action:</strong> Le vendeur recevra un SMS et un email confirmant que son paiement a été envoyé par virement Interac.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionLoading}>Annuler</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleSendPayment}
              className="bg-emerald-600 hover:bg-emerald-700"
              disabled={actionLoading}
            >
              {actionLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Envoi...
                </>
              ) : (
                <>
                  <CreditCard className="h-4 w-4 mr-2" />
                  Confirmer l'envoi
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Tickets Non-Compliant Dialog */}
      <AlertDialog open={showTicketsNonCompliantDialog} onOpenChange={setShowTicketsNonCompliantDialog}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center text-orange-600">
              <AlertTriangle className="h-5 w-5 mr-2" />
              Billets non conformes
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-4">
              <p>Signaler que les billets ne correspondent pas à la description de la transaction.</p>
              
              {selectedTransaction && (
                <div className="bg-gray-50 p-3 rounded text-sm">
                  <p><strong>Transaction:</strong> {selectedTransaction.reference_number || selectedTransaction.id?.slice(0,8)}</p>
                  <p><strong>Événement:</strong> {selectedTransaction.event_name}</p>
                  <p><strong>Vendeur:</strong> {selectedTransaction.seller_email}</p>
                </div>
              )}
              
              <div className="space-y-2">
                <Label>Raison de la non-conformité:</Label>
                <textarea
                  className="w-full p-2 border rounded-md text-sm min-h-[80px]"
                  placeholder="Ex: Les sièges ne correspondent pas (A10 au lieu de B15), mauvaise date..."
                  value={nonCompliantReason}
                  onChange={(e) => setNonCompliantReason(e.target.value)}
                />
              </div>
              
              <div className="bg-orange-50 border border-orange-200 p-3 rounded text-sm text-orange-800">
                <strong>⚠️ Une notification sera envoyée au vendeur</strong> pour l'informer du problème.
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionLoading}>Annuler</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleTicketsNonCompliant}
              className="bg-orange-600 hover:bg-orange-700"
              disabled={actionLoading || !nonCompliantReason.trim()}
            >
              {actionLoading ? 'Envoi...' : 'Signaler non-conformité'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Payment Non-Compliant Dialog */}
      <AlertDialog open={showPaymentNonCompliantDialog} onOpenChange={setShowPaymentNonCompliantDialog}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center text-red-600">
              <AlertTriangle className="h-5 w-5 mr-2" />
              Paiement non conforme
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-4">
              <p>Signaler que le montant reçu ne correspond pas au montant attendu.</p>
              
              {selectedTransaction && (
                <div className="bg-gray-50 p-3 rounded text-sm">
                  <p><strong>Transaction:</strong> {selectedTransaction.reference_number || selectedTransaction.id?.slice(0,8)}</p>
                  <p><strong>Acheteur:</strong> {selectedTransaction.buyer_email}</p>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-sm">Montant attendu ($):</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={paymentExpected}
                    onChange={(e) => setPaymentExpected(e.target.value)}
                    placeholder="374.95"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-sm">Montant reçu ($):</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={paymentReceived}
                    onChange={(e) => setPaymentReceived(e.target.value)}
                    placeholder="350.00"
                  />
                </div>
              </div>
              
              {paymentExpected && paymentReceived && (
                <div className={`p-3 rounded text-sm ${
                  parseFloat(paymentExpected) > parseFloat(paymentReceived) 
                    ? 'bg-red-50 border border-red-200 text-red-800' 
                    : 'bg-green-50 border border-green-200 text-green-800'
                }`}>
                  <strong>Différence:</strong> {(parseFloat(paymentExpected) - parseFloat(paymentReceived)).toFixed(2)}$
                  {parseFloat(paymentExpected) > parseFloat(paymentReceived) 
                    ? ' (manquant)' 
                    : ' (en trop - sera remboursé)'}
                </div>
              )}
              
              <div className="bg-red-50 border border-red-200 p-3 rounded text-sm text-red-800">
                <strong>⚠️ Une notification sera envoyée à l'acheteur</strong> pour l'informer du montant incorrect.
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionLoading}>Annuler</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handlePaymentNonCompliant}
              className="bg-red-600 hover:bg-red-700"
              disabled={actionLoading || !paymentExpected || !paymentReceived}
            >
              {actionLoading ? 'Envoi...' : 'Signaler non-conformité'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Create Transaction Dialog */}
      <AlertDialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <AlertDialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-brand-600" />
              Créer un nouveau transfert
            </AlertDialogTitle>
          </AlertDialogHeader>
          <AdminTransferForm
            token={token}
            onSuccess={() => {
              setShowCreateDialog(false);
              fetchTransactions();
            }}
            onCancel={() => setShowCreateDialog(false)}
          />
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Transaction Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Supprimer ce transfert ?
            </AlertDialogTitle>
            <AlertDialogDescription>
              <div className="space-y-3 pt-2">
                <p>Vous êtes sur le point de supprimer définitivement ce transfert :</p>
                {selectedTransaction && (
                  <div className="bg-gray-50 border rounded p-3 space-y-1">
                    <p><strong>Événement:</strong> {selectedTransaction.event_name}</p>
                    <p><strong>ID:</strong> {selectedTransaction.id}</p>
                    <p><strong>Prix:</strong> {selectedTransaction.price}$</p>
                    <p><strong>Statut:</strong> {selectedTransaction.status}</p>
                  </div>
                )}
                <div className="bg-red-50 border border-red-200 p-3 rounded text-sm text-red-800">
                  <strong>⚠️ Attention:</strong> Cette action est irréversible. Toutes les données associées à ce transfert seront perdues.
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionLoading}>Annuler</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteTransaction}
              className="bg-red-600 hover:bg-red-700"
              disabled={actionLoading}
            >
              {actionLoading ? 'Suppression...' : 'Supprimer définitivement'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TransactionManagement;
